import type { Transaction } from '@tiptap/pm/state';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { NodeWithPos } from 'prosemirror-utils';

import type { PmNodeJson } from './../../pm-converters/types';
import type { RicosDocumentNode } from './../../ricos-schema/next-schema';
import { logInvariant } from './../../_shared/log-invariant';
import { isDefined } from './../../wix-rich-content-common/index';
import { createRicosFunctionalExtension } from './../../tiptap-extensions';

const SPOTLIGHT_RANGE_CLASS = 'ricos-spotlight';
const SPOTLIGHT_CURSOR_CLASS = 'ricos-cursor-spotlight';

export type RangeSpotlight = {
  type: 'range';
  from?: string;
  to?: string;
  excluded?: boolean;
};

export type AfterSpotlight = {
  type: 'afterNode';
  // when no id is provided, the spotlight will be placed at the beginning of the document
  id?: string;
};
export type Spotlight = RangeSpotlight | AfterSpotlight;

export type DocumentChange = { nodes: RicosDocumentNode[]; position: Spotlight };

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    spotlight: {
      /**
       * Toggle a paragraph
       */
      setSpotlightList: (spotlightList: Spotlight[]) => ReturnType;
      injectNodes: (args: DocumentChange[], revert: { changes?: DocumentChange[] }) => ReturnType;
      clear: () => ReturnType;
    };
  }
}
type PmPluginState = { spotlightList: Spotlight[]; decorations: DecorationSet };

export const spotlight = createRicosFunctionalExtension({
  type: 'extension',
  groups: [],
  name: 'spotlight',

  createExtensionConfig({ services }) {
    const pmPlugin = new Plugin({
      key: new PluginKey('spotlight'),
      state: {
        init(): PmPluginState {
          return {
            spotlightList: [],
            decorations: DecorationSet.empty,
          };
        },
        apply(tr, state): PmPluginState {
          const meta = tr.getMeta(pmPlugin);
          if (!meta) {
            return { ...state, decorations: state.decorations.map(tr.mapping, tr.doc) };
          }

          if (meta.spotlightList.length === 0) {
            return {
              ...state,
              decorations: DecorationSet.empty,
            };
          }

          const nodeWithPosListRange = (meta.spotlightList as Spotlight[])
            .filter((s): s is RangeSpotlight => s.type === 'range')
            .flatMap(s => {
              const { from, to, excluded } = s;
              const fromNode = from
                ? excluded
                  ? findNodeSiblingAfterNodeId(tr, from)
                  : findNodeById(tr, from)
                : getFirstNode(tr);
              const toNode = to
                ? excluded
                  ? findNodeSiblingBeforeNodeId(tr, to)
                  : findNodeById(tr, to)
                : getLastNode(tr);
              if (!fromNode) {
                console.error(`Spotlight: Node with id ${from} not found`);
                return undefined;
              }
              if (!toNode) {
                console.error(`Spotlight: Node with id ${to} not found`);
                return undefined;
              }
              const parentFromNode = tr.doc.resolve(fromNode.pos).parent;
              const parentToNode = tr.doc.resolve(toNode.pos).parent;
              if (parentFromNode !== parentToNode) {
                console.error(`Spotlight: Nodes with ids ${from} and ${to} are not siblings`);
                return undefined;
              }
              const allNodesInSpotlightRange: NodeWithPos[] = [];
              tr.doc.nodesBetween(
                fromNode.pos,
                toNode.pos + toNode.node.nodeSize,
                (node, pos, parent) => {
                  if (parent === parentFromNode) {
                    allNodesInSpotlightRange.push({ node, pos });
                    return false;
                  }
                }
              );
              return allNodesInSpotlightRange;
            })
            .filter(isDefined);

          const posListWithAfter: number[] = (meta.spotlightList as Spotlight[])
            .filter((s): s is AfterSpotlight => s.type === 'afterNode')
            .map(s => {
              if (!s.id) {
                return 0;
              }
              const node = findNodeById(tr, s.id);
              if (!node) {
                console.error(`Spotlight: Node with id ${s.id} not found`);
                return undefined;
              }
              return node.pos + node.node.nodeSize;
            })
            .filter(isDefined);

          let decorations = DecorationSet.empty;

          for (const nodeWithPos of nodeWithPosListRange) {
            const { node, pos } = nodeWithPos;
            decorations = decorations.add(tr.doc, [
              Decoration.node(pos, pos + node.nodeSize, {
                class: SPOTLIGHT_RANGE_CLASS,
              }),
            ]);
          }

          for (const pos of posListWithAfter) {
            decorations = decorations.add(tr.doc, [
              Decoration.widget(pos, (_view, _getPos) => {
                const div = document.createElement('div');
                div.classList.add(SPOTLIGHT_CURSOR_CLASS);
                return div;
              }),
            ]);
          }

          return {
            spotlightList: meta.spotlightList,
            decorations,
          };
        },
      },
      props: {
        decorations(state) {
          return this?.getState(state)?.decorations;
        },
      },
    });
    return {
      name: this.name,

      addCommands() {
        return {
          injectNodes:
            (nodesAndPositions: DocumentChange[], revert: { changes?: DocumentChange[] }) =>
            ({ tr, dispatch, editor }) => {
              nodesAndPositions.forEach(({ nodes, position }) => {
                const pmJsonNodes = nodes.map(node => services.converters.toPmNode(node as any));
                const pmNodes = pmJsonNodes.map(editor.schema.nodeFromJSON);
                revert.changes = [];
                switch (position.type) {
                  case 'range': {
                    const firstNode = position.from
                      ? position.excluded
                        ? findNodeSiblingAfterNodeId(tr, position.from)
                        : findNodeById(tr, position.from)
                      : getFirstNode(tr);
                    const lastNode = position.to
                      ? position.excluded
                        ? findNodeSiblingBeforeNodeId(tr, position.to)
                        : findNodeById(tr, position.to)
                      : getLastNode(tr);
                    if (!firstNode) {
                      console.error(`Spotlight: Node with id ${position.from} not found`);
                      return;
                    }
                    if (!lastNode) {
                      console.error(`Spotlight: Node with id ${position.to} not found`);
                      return;
                    }

                    const replacedNodes = tr.doc.slice(
                      firstNode.pos,
                      lastNode.pos + lastNode.node.nodeSize
                    ).content;

                    const replacedRicosNodes = (replacedNodes.toJSON() ?? []).map(
                      (node: PmNodeJson) => services.converters.fromPmNode(node)
                    ) as RicosDocumentNode[];

                    const nodeBefore =
                      firstNode.pos > 0 ? tr.doc.nodeAt(firstNode.pos - 1) : undefined;

                    tr.replaceWith(firstNode.pos, lastNode.pos + lastNode.node.nodeSize, pmNodes);
                    tr.setMeta('allowTextReplace', true);
                    if (nodes.length === 0) {
                      revert.changes.push({
                        nodes: replacedRicosNodes,
                        position: {
                          type: 'afterNode',
                          id: nodeBefore ? nodeBefore.attrs.id : 'START',
                        },
                      });
                    } else {
                      revert.changes.push({
                        nodes: replacedRicosNodes,
                        position: {
                          type: 'range',
                          from: nodes[0].id!,
                          to: nodes[nodes.length - 1].id!,
                        },
                      });
                    }
                    return;
                  }
                  case 'afterNode': {
                    if (!position.id) {
                      tr.insert(0, pmNodes);
                      revert.changes.push({
                        nodes: [],
                        position: {
                          type: 'range',
                          from: nodes[0].id!,
                          to: nodes[nodes.length - 1].id!,
                        },
                      });
                      return;
                    }
                    const nodeWithPos = findNodeById(tr, position.id);

                    if (!nodeWithPos) {
                      console.error(`Spotlight: Node with id ${position.id} not found`);
                      return;
                    }
                    tr.insert(nodeWithPos.pos + nodeWithPos.node.nodeSize, pmNodes);
                    tr.setMeta('allowTextReplace', true);
                    revert.changes.push({
                      nodes: [],
                      position: {
                        type: 'range',
                        from: nodes[0].id!,
                        to: nodes[nodes.length - 1].id!,
                      },
                    });

                    return;
                  }
                  default:
                    logInvariant(new Error('Invalid position type when injecting'));
                }
              });
              if (dispatch) {
                // dispatch(tr);
              }
              return true;
            },
          setSpotlightList:
            (spotlightList: Spotlight[]) =>
            ({ tr, dispatch, view }) => {
              const firstNodeId =
                spotlightList[0]?.type === 'range' ? spotlightList[0].from : spotlightList[0]?.id;
              if (dispatch) {
                tr.setMeta(pmPlugin, {
                  spotlightList,
                });
                if (firstNodeId) {
                  if (firstNodeId === 'START') {
                    view.dom.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                  const nodeWithPos = findNodeById(tr, firstNodeId);
                  if (nodeWithPos) {
                    queueMicrotask(() => {
                      const { pos } = nodeWithPos;
                      const dom = view.nodeDOM(pos) as HTMLElement;
                      if (dom) {
                        dom.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    });
                  }
                }
              }
              return true;
            },
        };
      },

      addProseMirrorPlugins() {
        return [pmPlugin];
      },
    };
  },
});

const findNodeSiblingAfterNodeId = (tr: Transaction, id: string): NodeWithPos | undefined => {
  const nodesWithPos: NodeWithPos[] = [];
  tr.doc.descendants((child, pos, parent) => {
    if (child.attrs.id === id) {
      const afterChild = parent?.childAfter(pos + child.nodeSize);

      if (afterChild?.node) {
        const afterNode = findNodeById(tr, afterChild.node.attrs.id);
        if (afterNode) {
          nodesWithPos.push(afterNode);
        }
        return false;
      }
    }
  });
  const nodeWithPos = nodesWithPos[0];
  if (!nodeWithPos) {
    return undefined;
  }
  return nodeWithPos;
};

const findNodeSiblingBeforeNodeId = (tr: Transaction, id: string): NodeWithPos | undefined => {
  const nodesWithPos: NodeWithPos[] = [];
  tr.doc.descendants((child, pos, parent, index) => {
    if (child.attrs.id === id) {
      const beforeSiblingIndex = Math.max(index - 1, 0);
      const beforeChild = parent?.maybeChild(beforeSiblingIndex);

      if (beforeChild) {
        const beforeNode = findNodeById(tr, beforeChild.attrs.id);
        if (beforeNode) {
          nodesWithPos.push(beforeNode);
        }
        return false;
      }
    }
  });
  const nodeWithPos = nodesWithPos[0];
  if (!nodeWithPos) {
    return undefined;
  }
  return nodeWithPos;
};

const findNodeById = (tr: Transaction, id: string): NodeWithPos | undefined => {
  const nodesWithPos: NodeWithPos[] = [];
  tr.doc.descendants((child, pos) => {
    if (child.attrs.id === id) {
      nodesWithPos.push({
        node: child,
        pos,
      });
      return false;
    }
  });
  const nodeWithPos = nodesWithPos[0];
  if (!nodeWithPos) {
    return undefined;
  }
  const parent = tr.doc.resolve(nodeWithPos.pos).parent;
  if (parent.type.name === 'FIGURE' && nodeWithPos.node.type.name !== 'CAPTION') {
    return { node: parent, pos: nodeWithPos.pos - 1 };
  }
  return nodeWithPos;
};

const getFirstNode = (tr: Transaction): NodeWithPos | undefined => {
  const firstNode = tr.doc.content.firstChild;
  if (!firstNode) {
    return undefined;
  }
  return { node: firstNode, pos: 0 };
};

const getLastNode = (tr: Transaction): NodeWithPos | undefined => {
  const lastNode = tr.doc.content.lastChild;
  if (!lastNode) {
    return undefined;
  }
  return { node: lastNode, pos: tr.doc.content.size - lastNode.nodeSize };
};
